<div class="tld-page">
  <app-project-nav></app-project-nav>
  @if (notice; as n) {
    @if (n.variant === 'error') {
      <p class="tld-notice-error" role="alert" data-testid="tld-notice-error">{{ n.key | translate: n.params }}</p>
    } @else {
      <app-success-banner [messageKey]="n.key" [messageParams]="n.params" />
    }
  }

  <app-page-header
    [eyebrow]="'testRunExecutions.eyebrow' | translate"
    [title]="'testRunExecutions.title' | translate"
    [subtitle]="subtitle()"
  >
    <ng-container right>
      <app-stat-group [items]="stats()"></app-stat-group>
      @if (schedule(); as current) {
        @if (schedulePaused()) {
          <span class="tld-schedule tld-schedule--paused">
            <mat-icon aria-hidden="true">pause_circle</mat-icon>
            <span data-testid="schedule-paused">
              {{ 'testRunSchedule.paused' | translate }}
              @if (current.pausedReason) {
                <span data-testid="schedule-paused-reason">
                  – {{ 'testRunSchedule.pausedReason.' + current.pausedReason | translate }}
                </span>
              }
            </span>
          </span>
        } @else if (nextStart(); as when) {
          <span class="tld-schedule">
            <mat-icon aria-hidden="true">schedule</mat-icon>
            <span data-testid="schedule-next">{{ 'testRunSchedule.next' | translate: { when } }}</span>
          </span>
        }
      }
      @if (canSchedule()) {
        <button type="button" class="tld-secondary" data-testid="schedule-open" (click)="openSchedule()">
          <mat-icon class="tld-secondary__icon" aria-hidden="true">schedule</mat-icon>
          {{ 'testRunSchedule.button' | translate }}
        </button>
      }
      @if (retestBasis(); as basis) {
        <button
          type="button"
          class="tld-secondary"
          data-testid="durchlauf-retest"
          [disabled]="starting() || loading()"
          (click)="startRetest(basis)"
        >
          @if (retestStarting()) {
            <mat-spinner diameter="14" />
          } @else {
            <span class="tld-secondary__symbol" data-testid="durchlauf-retest-symbol" aria-hidden="true">↻</span>
          }
          {{ 'testRunExecutions.retest.button' | translate: { count: basis.retestCaseCount } }}
        </button>
      }
      @if (!isArchived() && !isReader()) {
        <button
          type="button"
          class="tld-primary"
          data-testid="durchlauf-start"
          [disabled]="starting() || loading() || headLoading()"
          (click)="startExecution()"
        >
          <mat-icon aria-hidden="true">play_arrow</mat-icon>
          {{ 'testRunExecutions.start' | translate }}
        </button>
      }
    </ng-container>
  </app-page-header>

  @if (runKey()) {
    <div class="tld-identity">
      <app-code-id-chip [id]="runKey()" />
      <span class="tld-art" [attr.data-art]="automated() ? 'automated' : 'manual'" data-testid="durchlauf-art">
        {{ (automated() ? 'testRunExecutions.art.automated' : 'testRunExecutions.art.manual') | translate }}
      </span>
      @if (run()?.trial) {
        <app-trial-run-pill />
      }
      @if (moduleRoot(); as root) {
        <app-run-root-module
          [projectId]="projectId"
          [moduleId]="root.id"
          [moduleKey]="root.key"
          [moduleTitle]="root.title"
        />
      }
    </div>
  }

  <div class="tld-banner" data-testid="durchlauf-banner">
    <mat-icon class="tld-banner__icon" aria-hidden="true">check_circle</mat-icon>
    <span>{{ 'testRunExecutions.banner' | translate }}</span>
  </div>

  @if (loading()) {
    <div class="tld-loading">
      <mat-spinner diameter="32" />
    </div>
  } @else if (listError()) {
    <p class="tld-error" role="alert">{{ 'testRunExecutions.loadError' | translate }}</p>
  } @else {
    <div appListFocusScroll class="tld-card" role="table" [attr.aria-label]="'testRunExecutions.tableAria' | translate">
      <div class="tld-head" role="row">
        <div class="tld-col tld-col--nr" role="columnheader">{{ 'testRunExecutions.column.nr' | translate }}</div>
        <div class="tld-col tld-col--when" role="columnheader">{{ 'testRunExecutions.column.when' | translate }}</div>
        <div class="tld-col tld-col--result" role="columnheader">
          {{ 'testRunExecutions.column.result' | translate }}
        </div>
        <div class="tld-col tld-col--steps" role="columnheader">
          {{ 'testRunExecutions.column.steps' | translate }}
        </div>
        <div class="tld-col tld-col--duration" role="columnheader">
          {{ 'testRunExecutions.column.duration' | translate }}
        </div>
        <div class="tld-col tld-col--assignee" role="columnheader">
          {{ 'testRunExecutions.column.assignee' | translate }}
        </div>
        <div class="tld-col tld-col--comments" role="columnheader">
          {{ 'testRunExecutions.column.comments' | translate }}
        </div>
        <div class="tld-col tld-col--chevron" role="columnheader"></div>
      </div>

      @if (executions().length === 0) {
        <p class="tld-empty" role="row" data-testid="durchlauf-empty">
          <span role="cell">{{ 'testRunExecutions.empty' | translate }}</span>
        </p>
      } @else {
        @for (execution of executions(); track execution.id; let idx = $index) {
          <div
            class="tld-row"
            [class.tld-row--live]="execution.live"
            role="row"
            tabindex="0"
            [attr.data-testid]="'durchlauf-row-' + execution.nr"
            [attr.aria-describedby]="'durchlauf-open-' + execution.nr"
            (click)="openExecution(execution)"
            (keydown.enter)="openExecution(execution)"
            (keydown.space)="$event.preventDefault(); openExecution(execution)"
          >
            <div class="tld-col tld-col--nr" role="cell">
              <span class="tld-nr" [class.tld-nr--latest]="idx === 0">
                <span class="tld-nr__hash" aria-hidden="true">#</span>
                {{ execution.nr }}
              </span>
            </div>
            <div class="tld-col tld-col--when" role="cell">
              {{ execution.startedAt | date: 'dd.MM.yyyy HH:mm' }}
              @if (execution.retestOfNr) {
                <span
                  class="tld-retest-badge"
                  data-testid="durchlauf-retest-badge"
                  [attr.title]="'testRunExecutions.retest.badgeTitle' | translate: { nr: execution.retestOfNr }"
                >
                  {{ 'testRunExecutions.retest.badge' | translate: { nr: execution.retestOfNr } }}
                </span>
              }
            </div>
            <div class="tld-col tld-col--result" role="cell">
              @if (kategorie(execution); as kategorie) {
                <app-pipe-kategorie-pill [kategorie]="kategorie" [aussagekraeftig]="false" />
              } @else {
                <app-res-pill
                  [status]="pillStatus(execution)"
                  [label]="pillLabel(execution)"
                  [pulsing]="execution.live"
                  [attr.data-testid]="execution.live ? 'durchlauf-live-chip' : null"
                />
              }
            </div>
            <div class="tld-col tld-col--steps" role="cell" [class.tld-col--emphasis]="execution.live">
              {{ steps(execution) }}
            </div>
            <div class="tld-col tld-col--duration" role="cell">
              @if (execution.live) {
                {{ 'testRunExecutions.runningSince' | translate: { since: execution.runningSince | date: 'HH:mm' } }}
              } @else {
                {{ duration(execution) }}
              }
            </div>
            <div class="tld-col tld-col--assignee" role="cell">
              @if (execution.assigneeName) {
                <app-avatar [name]="execution.assigneeName" [size]="26" />
                <span class="tld-assignee-name">{{ execution.assigneeName }}</span>
              } @else {
                <span class="tld-assignee-name tld-assignee-name--empty">—</span>
              }
            </div>
            <div class="tld-col tld-col--comments" role="cell" [class.tld-col--faint]="execution.commentCount === 0">
              {{ comments(execution) }}
            </div>
            <div class="tld-col tld-col--chevron" role="cell">
              <mat-icon aria-hidden="true">chevron_right</mat-icon>
              <span class="sr-only" [attr.id]="'durchlauf-open-' + execution.nr">
                {{ 'testRunExecutions.openRow' | translate: { nr: execution.nr } }}
              </span>
            </div>
          </div>
        }
      }
    </div>
  }
</div>
